import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Intentionally tiny widget for the "What is a gradient" post: pick a couple of colors
// and watch the gradient update live. A draggable caret rides along the bar and the hex
// below it reads the exact color at that point, so the reader feels how a position turns
// into a color (the whole idea of the post). The defaults are the emerald green and
// purple of the hummingbird on the post cover, so the widget opens on the cover's own
// colors rather than a random pair. The post marks the mount point with
// <!--GRAD-START-->...<!--GRAD-END--> around a static fallback (kept for crawlers and
// no-JS readers); BlogPostPageComponent swaps this live component in. Same design
// language as the value-rise / value-ladder / saturation-wheel widgets.
@Component( {
  selector: 'app-gradient-preview',
  templateUrl: './gradient-preview.component.html',
  styleUrls: [ './gradient-preview.component.scss' ],
} )
export class GradientPreviewComponent {

  @Input() heading = 'Try It Yourself';

  @ViewChild( 'bar', { static: true } ) barRef!: ElementRef<HTMLElement>;

  // The cover hummingbird's two colors to start (emerald green to throat purple);
  // the reader edits either one.
  stops: string[] = [ '#1FC274', '#A65AD6' ];

  // Caret position along the bar, 0..100 percent. Starts at the midpoint.
  caretPos = 50;

  private dragging = false;
  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  onColor( index: number, value: string ): void {
    if ( index < 0 || index >= this.stops.length || !value ) {
      return;
    }
    this.stops[ index ] = value.toUpperCase();
    this.track();
  }

  // Left to right linear blend through every stop, the simplest gradient there is.
  get gradientCss(): string {
    return `linear-gradient(90deg, ${ this.stops.join( ', ' ) })`;
  }

  // The color sitting under the caret, interpolated across the evenly spaced stops
  // exactly the way the bar paints them, so the readout always matches what is on screen.
  get caretHex(): string {
    return this.colorAt( this.caretPos / 100 );
  }

  // ---- dragging the caret -----------------------------------------------------

  onDown( event: PointerEvent ): void {
    this.dragging = true;
    const stage = event.currentTarget as HTMLElement;
    if ( stage.setPointerCapture ) {
      stage.setPointerCapture( event.pointerId );
    }
    this.moveTo( event.clientX );
    this.track();
  }

  onMove( event: PointerEvent ): void {
    if ( this.dragging ) {
      this.moveTo( event.clientX );
    }
  }

  onUp( event: PointerEvent ): void {
    this.dragging = false;
    const stage = event.currentTarget as HTMLElement;
    if ( stage.releasePointerCapture ) {
      try {
        stage.releasePointerCapture( event.pointerId );
      } catch ( _e ) {
        // capture may already be gone; ignore.
      }
    }
  }

  // Keyboard support so the caret is a real slider (arrows nudge, shift jumps).
  onKey( event: KeyboardEvent ): void {
    const step = event.shiftKey ? 10 : 2;
    if ( event.key === 'ArrowLeft' || event.key === 'ArrowDown' ) {
      this.caretPos = this.clamp( this.caretPos - step, 0, 100 );
      event.preventDefault();
      this.track();
    } else if ( event.key === 'ArrowRight' || event.key === 'ArrowUp' ) {
      this.caretPos = this.clamp( this.caretPos + step, 0, 100 );
      event.preventDefault();
      this.track();
    }
  }

  private moveTo( clientX: number ): void {
    const rect = this.barRef.nativeElement.getBoundingClientRect();
    if ( rect.width <= 0 ) {
      return;
    }
    this.caretPos = this.clamp( ( ( clientX - rect.left ) / rect.width ) * 100, 0, 100 );
  }

  // ---- color math -------------------------------------------------------------

  private colorAt( t: number ): string {
    const n = this.stops.length;
    if ( n === 0 ) {
      return '#000000';
    }
    if ( n === 1 ) {
      return this.stops[ 0 ].toUpperCase();
    }
    const seg = this.clamp( t, 0, 1 ) * ( n - 1 );
    const i = Math.min( Math.floor( seg ), n - 2 );
    return this.mix( this.stops[ i ], this.stops[ i + 1 ], seg - i );
  }

  private mix( a: string, b: string, f: number ): string {
    const ca = this.rgb( a );
    const cb = this.rgb( b );
    const m = ( x: number, y: number ) => Math.round( x + ( y - x ) * f );
    const to = ( v: number ) => v.toString( 16 ).padStart( 2, '0' );
    return ( '#' + to( m( ca[ 0 ], cb[ 0 ] ) ) + to( m( ca[ 1 ], cb[ 1 ] ) ) + to( m( ca[ 2 ], cb[ 2 ] ) ) ).toUpperCase();
  }

  private rgb( hex: string ): [ number, number, number ] {
    const h = ( hex || '' ).replace( /^#/, '' );
    return [
      parseInt( h.slice( 0, 2 ), 16 ) || 0,
      parseInt( h.slice( 2, 4 ), 16 ) || 0,
      parseInt( h.slice( 4, 6 ), 16 ) || 0,
    ];
  }

  private clamp( v: number, lo: number, hi: number ): number {
    return Math.max( lo, Math.min( hi, v ) );
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'gradient-preview' } );
  }
}
